import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, change } from 'redux-form';
import { TextAreaField } from '../../../../../../components/fields';
import {
    getTimesheetShifts,
    approveTimesheet,
    rejectTimesheet,
    deleteTimeRecord,
    saveTimeEntry,
    resubmitTimesheet,
    confirmTimesheet,
    updateTimesheet,
    getJobProjects
} from '../../../../../../reducers/timekeeping';
import { FormattedMessage } from 'react-intl';
import messages from '../../../../../../text/messages';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import { TIMESHEET_ACTIONS, TIMESHEET_STATUS } from '../../../../../../text/enumerations';
import { errorMessages } from '../../../../../../utilities/validation';
import WorkWeek from '../../../../components/workWeek';

class ClientEditShiftsForm extends React.Component {
    constructor(props) {
        super(props);

        this.resolveActions = this.resolveActions.bind(this);

        this.reject = this.reject.bind(this);
        this.approve = this.approve.bind(this);
        this.submit = this.submit.bind(this);
        this.confirm = this.confirm.bind(this);
        this.resubmit = this.resubmit.bind(this);
        this.updateShifts = this.updateShifts.bind(this);
    }

    componentDidMount() {
        const { job, getJobProjects } = this.props;
        getJobProjects(job.id);
    }

    resolveActions() {
        const { action, cancel, changeField, form, valid } = this.props;

        switch (action) {
            case TIMESHEET_ACTIONS.REVIEW:
                return {
                    actions: {
                        submit: {
                            message: <FormattedMessage {...messages.approve} />,
                            onMouseDown: () => changeField(form, 'action', 'submit')
                        },
                        reject: {
                            onMouseDown: () => changeField(form, 'action', 'reject'),
                            type: 'submit'
                        }
                    },
                    submitAction: values => {
                        if (values.action === 'reject' && valid) {
                            this.reject(values);
                        } else if (values.action === 'submit') {
                            this.approve(values);
                        }
                    }
                };

            case TIMESHEET_ACTIONS.CONFIRM:
                return {
                    actions: { submit: { message: <FormattedMessage {...messages.submit} /> } },
                    submitAction: values => this.updateShifts(values, this.confirm)
                };

            case TIMESHEET_ACTIONS.EDIT:
                return {
                    actions: { submit: { message: <FormattedMessage {...messages.ok} /> } },
                    submitAction: values => this.updateShifts(values, this.resubmit)
                };

            case TIMESHEET_ACTIONS.VIEW:
            default:
                return { actions: { submit: { message: <FormattedMessage {...messages.ok} /> } }, submitAction: cancel };
        }
    }

    submit(values) {
        const { submitAction } = this.resolveActions();
        submitAction(values);
    }

    approve(values) {
        const { timesheet, approveTimesheet, updateTimesheet, cancel, reloadData } = this.props;
        const { approver_notes } = values;
        updateTimesheet({ ...timesheet, approver_notes }, () => {
            approveTimesheet(timesheet.id, approver_notes, () => {
                reloadData();
                cancel();
            });
        });
    }

    reject(values) {
        const { timesheet, rejectTimesheet, updateTimesheet, cancel, reloadData } = this.props;
        const { approver_notes } = values;
        updateTimesheet({ ...timesheet, approver_notes }, () => {
            rejectTimesheet(timesheet.id, approver_notes, () => {
                reloadData();
                cancel();
            });
        });
    }

    resubmit(values) {
        const { worker_notes } = values;
        const { timesheet, resubmitTimesheet, cancel, reloadData, getTimesheetShifts, updateTimesheet } = this.props;

        updateTimesheet({ ...timesheet, worker_notes }, () =>
            resubmitTimesheet(timesheet.id, () => {
                reloadData();
                getTimesheetShifts(timesheet.id);
                cancel();
            })
        );
    }
    confirm(values) {
        const { worker_notes } = values;
        const { timesheet, confirmTimesheet, cancel, reloadData, getTimesheetShifts, updateTimesheet } = this.props;
        confirmTimesheet(timesheet.id, () =>
            updateTimesheet({ ...timesheet, worker_notes }, () => {
                reloadData();
                getTimesheetShifts(timesheet.id);
                cancel();
            })
        );
    }

    updateShifts(values, onComplete) {
        const { shifts, job, project, saveTimeEntry } = this.props;

        let modified = [];
        shifts.forEach(s => {
            if (values[s.id] && values[s.id].duration !== s.minutes) {
                modified.push({ ...s, ...values[s.id] });
            }
        });

        this.setState({ ...this.state, loading: modified.length }, () => {
            modified.forEach(s => {
                saveTimeEntry({ ...s, time_in: moment.tz(s.time_in, job.work_timezone), jobId: job.id, project_id: project.id, minutes: s.duration }, () =>
                    this.setState({ ...this.state, loading: this.state.loading - 1 }, () => {
                        if (this.state.loading === 0 && onComplete) {
                            onComplete(values);
                        }
                    })
                );
            });
        });
    }

    render() {
        const { job, shifts, projects, timesheet, handleSubmit, submitting, cancel } = this.props;
        if (!timesheet || !shifts || !job || !projects) return null;

        const { actions } = this.resolveActions();

        return (
            <div className="row">
                <div className="col-xs-12 group">
                    <WorkWeek date={timesheet.period_ending} job={job} timesheet={timesheet} readOnly={true} />
                </div>
                <form onSubmit={handleSubmit(values => this.submit(values))}>
                    {timesheet.worker_notes !== undefined &&
                        timesheet.worker_notes !== null && (
                            <div className="col-xs-12">
                                <Field
                                    {...{
                                        component: TextAreaField,
                                        placeholder: messages.workerNotes,
                                        name: 'worker_notes',
                                        disabled: true
                                    }}
                                />
                            </div>
                        )}
                    <div className="col-xs-12">
                        <Field
                            {...{
                                component: TextAreaField,
                                placeholder: messages.notes,
                                name: 'approver_notes',
                                disabled: [TIMESHEET_STATUS.APPROVED, TIMESHEET_STATUS.AUTOAPPROVED].includes(timesheet.status)
                            }}
                        />
                    </div>
                    <div className="col-xs-12">
                        <WizardButtons disabled={submitting} cancel={{ action: cancel }} {...actions} />
                    </div>
                </form>
            </div>
        );
    }
}

ClientEditShiftsForm.defaultProps = {
    action: undefined,
    job: undefined,
    timesheet: undefined,
    shifts: undefined,
    cancel: () => null,
    reloadData: () => null
};
ClientEditShiftsForm.propTypes = {
    action: PropTypes.string.isRequired,
    job: PropTypes.object.isRequired,
    timesheet: PropTypes.object.isRequired,
    shifts: PropTypes.array.isRequired,
    cancel: PropTypes.func.isRequired,
    reloadData: PropTypes.func
};

ClientEditShiftsForm = reduxForm({
    destroyOnUnmount: true,
    enableReinitialize: false,
    forceUnregisterOnUnmount: true,
    keepDirtyOnReinitialize: false,
    validate: values => {
        const { approver_notes, action } = values;
        const errors = {
            approver_notes: !approver_notes && action === 'reject' ? errorMessages.required() : undefined
        };

        return errors;
    }
})(ClientEditShiftsForm);

const mapStateToProps = (state, props) => {
    const { timesheet } = props;
    const projects = state.timekeeping.projects;

    return {
        role: state.identity.role,
        projects,
        initialValues: { approver_notes: timesheet.approver_notes, worker_notes: timesheet.worker_notes }
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            changeField: change,
            getJobProjects,
            getTimesheetShifts,
            approveTimesheet,
            rejectTimesheet,
            deleteTimeRecord,
            resubmitTimesheet,
            confirmTimesheet,
            updateTimesheet,
            saveTimeEntry
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ClientEditShiftsForm);



// WEBPACK FOOTER //
// ./src/pages/timekeeping/forms/editShifts/tenants/client/index.jsx